import type { Ref } from "vue";
import { reactive, ref, watch } from "vue";
import { useAcl } from "vue-simple-acl";
import { SearchMode } from "~/core/composables/useSearch";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import LxcError from "~/core/models/LxcError.class";
import type { ReactiveArrayObject } from "~/core/models/ReactiveArrayObject.interface";
import { Filters } from "~/core/models/filters";
import { NotificationKey } from "~/core/utils/notifications";
import { useFetchDtwins } from "~/modules/dtwin/composables/useFetchDtwins";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { LogEntityClass } from "~/modules/log/models/LogEntityClass.enum";
import SectorsService from "~/modules/sector/services/sectors.service";
import type { Role } from "~/modules/user/models/Role.interface";
import type { User } from "~/modules/user/models/User.interface";
import type { UserGroup } from "~/modules/user/models/UserGroup.interface";
import type { UserSessionSector } from "~/modules/user/models/UserSessionSector.interface";
import userGroupService from "~/modules/user/services/userGroup.service";

const { can } = useAcl();

interface UseLogEntityFilters {
  organizationCode?: number;
}

// Fallback entity for error cases
interface FallbackEntity {
  label: string;
  [key: string]: string | number | boolean | undefined;
}

// Service with methods that accept IDs and return entities
interface EntityService<T> {
  [methodName: string]:
    | ((ids: string[]) => Promise<T[]>)
    | ((organizationCode: number, ids: string[]) => Promise<T[]>)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | any; // For other methods that exist on the service
}

export function useEntityFilters({ organizationCode }: UseLogEntityFilters) {
  const entityClass: Ref<LogEntityClass | undefined> = ref();
  const entityId: Ref<string[] | undefined> = ref();

  const devices = reactive<ReactiveArrayObject<Dtwin>>({ value: [] });
  const users = reactive<ReactiveArrayObject<User>>({ value: [] });

  const appliedDevices = reactive<ReactiveArrayObject<Dtwin>>({ value: [] });
  const appliedUsers = reactive<ReactiveArrayObject<User>>({ value: [] });
  const appliedUserGroups = reactive<ReactiveArrayObject<UserGroup>>({
    value: [],
  });
  const appliedRoles = reactive<ReactiveArrayObject<Role>>({ value: [] });
  const appliedSectors = reactive<ReactiveArrayObject<UserSessionSector>>({
    value: [],
  });

  const isDevicesLoading = ref(false);
  const isUsersLoading = ref(false);
  const isUserGroupsLoading = ref(false);
  const isProfileLoading = ref(false);
  const isRolesLoading = ref(false);
  const isSectorLoading = ref(false);

  const {
    results: resultDtwins,
    setFilter: setFilterDtwin,
    fetchData: fetchDataDtwin,
    error: errorDtwin,
  } = useFetchDtwins({
    organizationCode,
    searchMode: SearchMode.FILTER_SEARCH,
    useQueryParametersForPagination: false,
  });

  const isEntityLoading = computed((): boolean => {
    return (
      isDevicesLoading.value ||
      isUsersLoading.value ||
      isUserGroupsLoading.value ||
      isProfileLoading.value ||
      isSectorLoading.value ||
      isRolesLoading.value
    );
  });

  // Type-safe helper to get the primary key value
  function getPrimaryKeyValue<T, K extends string>(
    entity: T,
    primaryKey: K,
  ): string | number | undefined {
    return (entity as Record<K, string | number | undefined>)[primaryKey];
  }

  // Type-safe helper to create a fallback entity
  function createFallbackEntity<K extends string>(
    entityId: string,
    primaryKey: K,
  ): FallbackEntity {
    return {
      [primaryKey]: entityId,
      label: entityId,
    };
  }

  function errorFetchAppliedEntities<T, K extends string>(
    response: LxcError,
    entityIdsToFetch: string[],
    primaryKey: K,
  ): T[] {
    let errorResult: T[] = [];
    if (response.status === 404 || response.status === 410) {
      errorResult = entityIdsToFetch.map((entityId) => {
        return createFallbackEntity(entityId, primaryKey) as T;
      });
    } else {
      response.notify(NotificationKey.error);
    }
    return errorResult;
  }

  /**
   * Fetches and processes entities based on a list of entity IDs.
   *
   * <T> The type of entities being processed.
   * @param appliedList The list of already applied entities.
   * @param service The service used to fetch entity details.
   * @param detailsMethod The method name used to fetch entity details from the service.
   * @param primaryKey The primary key used to identify entities.
   * @param loading A reference to a boolean indicating the loading state.
   * @param entityIds (Optional) The list of entity IDs to fetch.
   * @param list (Optional) An additional list of entities to check against.
   * @param specialLogic Callback for special logic
   * @return A promise that resolves to an array of entities.
   * @throws SomeException If an error occurs during fetching.
   */
  async function fetchAppliedEntities<T, K extends string>(
    appliedList: ReactiveArrayObject<T>,
    service: EntityService<T> | undefined,
    detailsMethod: string,
    primaryKey: K,
    loading: Ref<boolean>,
    entityIds?: string[],
    list?: ReactiveArrayObject<T>,
    specialLogic?: (entityIdsToFetch: string[]) => Promise<T[]>,
  ): Promise<T[]> {
    let entitiesResult: T[] = [];

    if (entityIds && !loading.value) {
      loading.value = true;
      let entityIdsToFetch: string[];

      if (list && list.value.length > 0) {
        entityIdsToFetch = entityIds.filter(
          (paramId) =>
            list.value.every(
              (currentEntity: T) =>
                getPrimaryKeyValue(currentEntity, primaryKey) !== paramId,
            ) &&
            appliedList.value.every(
              (currentEntity) =>
                getPrimaryKeyValue(currentEntity, primaryKey) !== paramId,
            ),
        );

        entitiesResult = list.value
          .filter((currentEntity: T) =>
            entityIds.includes(
              String(getPrimaryKeyValue(currentEntity, primaryKey) ?? ""),
            ),
          )
          .concat(
            appliedList.value.filter((currentEntity) => {
              return (
                entityIds.includes(
                  String(getPrimaryKeyValue(currentEntity, primaryKey) ?? ""),
                ) &&
                !list.value.some(
                  (pEntity: T) =>
                    getPrimaryKeyValue(pEntity, primaryKey) ===
                    getPrimaryKeyValue(currentEntity, primaryKey),
                )
              );
            }),
          );
      } else {
        entityIdsToFetch = entityIds.filter((paramId) =>
          appliedList.value.every(
            (currentEntity) =>
              getPrimaryKeyValue(currentEntity, primaryKey) !== paramId,
          ),
        );
      }

      if (entityIdsToFetch.length > 0) {
        if (specialLogic) {
          entitiesResult = entitiesResult.concat(
            await specialLogic(entityIdsToFetch),
          );
        } else if (service) {
          const serviceMethod = service[detailsMethod];
          let response: T[] | LxcError;

          if (
            detailsMethod === "getUserGroupsByCode" &&
            organizationCode !== undefined
          ) {
            response = await (
              serviceMethod as (
                organizationCode: number,
                ids: string[],
              ) => Promise<T[]>
            )(organizationCode, entityIdsToFetch);
          } else {
            response = await (serviceMethod as (ids: string[]) => Promise<T[]>)(
              entityIdsToFetch,
            );
          }

          if (LxcError.check(response)) {
            entitiesResult = entitiesResult.concat(
              errorFetchAppliedEntities(response, entityIdsToFetch, primaryKey),
            );
          } else {
            entitiesResult = entitiesResult.concat(response);
          }
        }
      }
      loading.value = false;
    }

    return entitiesResult;
  }

  async function fetchAppliedDevices(deviceIds?: string[]) {
    appliedDevices.value = await fetchAppliedEntities<Dtwin, "uid">(
      appliedDevices,
      undefined,
      "",
      "uid",
      isDevicesLoading,
      deviceIds,
      devices,
      async (entityIdsToFetch) => {
        setFilterDtwin(Filters.DTWIN_UID, entityIdsToFetch);
        await fetchDataDtwin(0, entityIdsToFetch.length);
        let response: Dtwin[];
        if (
          errorDtwin.value ||
          !resultDtwins.value ||
          resultDtwins.value?.results?.length === 0
        ) {
          if (
            !errorDtwin.value &&
            (!resultDtwins.value || resultDtwins.value?.results?.length === 0)
          ) {
            errorDtwin.value = new LxcError({
              type: "DEVICE_TWIN_NOT_FOUND",
              title: "Not Found",
              detail: "no device twin found",
              status: 404,
              instance: "",
            });
          }
          response = errorFetchAppliedEntities(
            errorDtwin.value!,
            entityIdsToFetch,
            "uid",
          );
        } else {
          response = resultDtwins.value?.results || [];
        }
        return response;
      },
    );
  }

  async function loadEntitiesByClass(
    paramEntityClass?: string,
    paramEntityIds?: string[],
  ) {
    if (isEntityLoading.value) {
      return { pending: true };
    }

    switch (paramEntityClass) {
      case LogEntityClass.DEVICE:
        if (can(ACLRoles.CAN_READ_DEVICE)) {
          await fetchAppliedDevices(paramEntityIds);
          entityClass.value = paramEntityClass;
          entityId.value = paramEntityIds;
          devices.value = appliedDevices.value?.slice(0) ?? [];
        }
        break;
      case LogEntityClass.USER:
        appliedUsers.value =
          paramEntityIds?.map((userId): User => {
            return {
              firstName: "",
              lastName: "",
              login: userId,
              email: "",
              active: true,
              profile: { id: "0", name: "" },
            };
          }) ?? [];
        entityClass.value = paramEntityClass;
        entityId.value = paramEntityIds;
        users.value = appliedUsers.value?.slice(0) ?? [];
        break;
      case LogEntityClass.GROUP:
        appliedUserGroups.value = await fetchAppliedEntities<UserGroup, "code">(
          appliedUserGroups,
          userGroupService,
          "getUserGroupsByCode",
          "code",
          isUserGroupsLoading,
          paramEntityIds,
        );
        entityClass.value = paramEntityClass;
        entityId.value = paramEntityIds;
        break;
      case LogEntityClass.ROLE:
        entityClass.value = paramEntityClass;
        entityId.value = paramEntityIds;
        break;
      case LogEntityClass.SECTOR:
        appliedSectors.value = await fetchAppliedEntities<
          UserSessionSector,
          "code"
        >(
          appliedSectors,
          SectorsService,
          "getSectorsByCodes",
          "code",
          isSectorLoading,
          paramEntityIds,
        );
        entityClass.value = paramEntityClass;
        entityId.value = paramEntityIds;
        break;
      default:
        entityClass.value = undefined;
        entityId.value = undefined;
        devices.value = [];
        users.value = [];
        break;
    }

    return { pending: false };
  }

  function onDevicesSelected(devices: ReactiveArrayObject<Dtwin>) {
    if (!devices.value.length) {
      entityClass.value = undefined;
      entityId.value = undefined;
    } else {
      entityClass.value = LogEntityClass.DEVICE;
      entityId.value = devices.value
        .filter((device) => device.uid !== undefined)
        .map((device) => device.uid as string);
    }
  }

  function onUsersSelected(users: ReactiveArrayObject<User>) {
    if (!users.value.length) {
      entityClass.value = undefined;
      entityId.value = undefined;
    } else {
      entityClass.value = LogEntityClass.USER;
      entityId.value = users.value
        .filter((user) => user.login !== undefined)
        .map((user) => user.login);
    }
  }

  function clearEntities() {
    entityClass.value = undefined;
    entityId.value = [];
    devices.value = [];
    users.value = [];
    appliedDevices.value = [];
  }

  function getFilterValues() {
    return {
      entityClass: entityClass.value ?? "",
      entityId: entityId.value?.slice(0) ?? [],
    };
  }

  // Auto-update on selection changes
  watch(() => devices, onDevicesSelected, { deep: true });
  watch(() => users, onUsersSelected, { deep: true });

  return {
    entityClass,
    entityId,
    devices,
    users,
    appliedDevices,
    appliedUsers,
    appliedUserGroups,
    appliedRoles,
    appliedSectors,
    isEntityLoading,
    loadEntitiesByClass,
    clearEntities,
    getFilterValues,
  };
}
